import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { TrendingUp, ArrowLeft } from "lucide-react";

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="font-heading text-lg uppercase tracking-wider text-zinc-200 mb-3">{title}</h2>
    <div className="text-sm text-zinc-400 leading-relaxed space-y-3">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1.5 text-sm text-zinc-400">
    {items.map((t, i) => <li key={i}>{t}</li>)}
  </ul>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100">
      <header className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="privacy-brand">
            <TrendingUp className="w-5 h-5 text-blue-500" />
            <span className="font-heading font-semibold text-lg tracking-tight">ZigZagX</span>
          </Link>
          <Link to="/login">
            <Button
              variant="ghost"
              className="rounded-sm text-zinc-400 hover:text-zinc-100 h-8 text-xs font-mono uppercase tracking-wider"
              data-testid="privacy-back-btn"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to login
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="privacy-page">
        <h1 className="font-heading text-3xl font-semibold tracking-tight mb-1">Privacy Policy</h1>
        <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider mb-8">Updated at 2026-09-01</p>

        <Section title="Introduction">
          <p>
            ZigZagX ("we", "us", or "our") respects your privacy and is committed to protecting the personal information
            you share with us. This Privacy Policy explains what we collect, how we use it, and the choices you have
            about your data when you use the ZigZagX website and services (the "Service").
          </p>
          <p>
            By using the Service, you consent to the collection and use of your information as described in this
            Privacy Policy. If you do not agree, please do not use the Service.
          </p>
        </Section>

        <Section title="Information We Collect">
          <p>We collect the following categories of information:</p>
          <Bullets
            items={[
              "Account information: name, email address, and (optionally) mobile phone number that you provide when accepting an invite or updating your profile.",
              "Authentication data: hashed passwords, login timestamps, IP address, and user-agent string of each successful login. Passwords themselves are never stored in plaintext.",
              "Trading journal content: trades, phase entries, notes, price/share values, timeframes, setup criteria, confidence ratings, and screenshots you upload.",
              "Preferences: notification method (email, SMS, both, none), event opt-ins, default trade size, and other UI settings.",
              "Automatically collected data: cookies and similar technologies used to keep you signed in and to remember non-sensitive UI preferences.",
              "Communications: any messages, feedback, or support requests you send us.",
            ]}
          />
        </Section>

        <Section title="How We Use Your Information">
          <p>We use your information to:</p>
          <Bullets
            items={[
              "Provide, operate, and improve the Service.",
              "Authenticate you and secure your account, including detecting suspicious activity.",
              "Deliver transactional emails via Resend (invitations, password resets, trade notifications) and, if you opt in, SMS via Twilio.",
              "Fetch live market data (quotes, insider transactions, fundamentals) from third-party market data providers such as Yahoo Finance.",
              "Store screenshots you upload in Emergent Object Storage so they are accessible when you view your trades.",
              "Generate AI-assisted trade context (e.g. market history summaries) using large language models. Only the ticker and public market events are sent — never your notes or personal data.",
              "Comply with legal obligations and enforce our Terms of Service.",
            ]}
          />
        </Section>

        <Section title="Third-Party Service Providers">
          <p>
            We rely on a small set of vetted third-party providers to operate the Service. These providers process
            data on our behalf under their own privacy and security terms:
          </p>
          <Bullets
            items={[
              "Resend — transactional email delivery (invitations, notifications).",
              "Twilio — SMS delivery for users who opt in to text notifications.",
              "MongoDB / Emergent — hosted database and file object storage.",
              "Yahoo Finance (yfinance) — public market data (ticker quotes, insider filings, fundamentals). We send only ticker symbols, never personal data.",
              "Emergent LLM Gateway — AI-generated market history summaries. We send only the ticker and public price context.",
            ]}
          />
          <p>
            We do not sell, rent, or trade your personal information to advertisers or data brokers.
          </p>
        </Section>

        <Section title="Cookies & Local Storage">
          <p>
            ZigZagX uses browser cookies and localStorage to keep you signed in (via a JWT access token) and to
            remember UI preferences such as the last-selected timeframe on the Insider Summary widget. These are
            technical cookies required for the Service to function; we do not use advertising or third-party tracking
            cookies. Most browsers let you block or delete cookies through their settings, but doing so may sign you
            out or disable certain features.
          </p>
        </Section>

        <Section title="Data Retention">
          <p>
            We retain your account and journal data for as long as your account is active. Login events are retained
            for security auditing. Screenshots you upload remain in object storage until you delete the associated
            trade or phase. If you request account deletion, we will remove your personal information and journal
            content within a reasonable period, except where retention is required by law.
          </p>
        </Section>

        <Section title="Security">
          <p>
            We take reasonable technical and organizational measures to protect your information, including bcrypt
            password hashing, JWT authentication with short-lived tokens, HTTPS-only transport, and strict server-side
            role-based access controls. However, no method of transmission over the Internet or method of electronic
            storage is 100% secure, and we cannot guarantee absolute security.
          </p>
        </Section>

        <Section title="Your Rights & Choices">
          <p>You can:</p>
          <Bullets
            items={[
              "Update your name, email, phone number, and password at any time from Settings → Account.",
              "Change or turn off notifications at any time from Settings → Trade Notifications.",
              "Request a copy or deletion of your data by emailing us — see Contact below.",
              "Withdraw consent to receive text alerts by switching your notification method to Email or Off.",
            ]}
          />
          <p>
            Depending on where you live (for example, California residents under the CCPA or EU/UK residents under the
            GDPR), you may have additional rights such as access, correction, portability, and erasure. Contact us to
            exercise these rights.
          </p>
        </Section>

        <Section title="SMS / Text Messaging Notifications">
          <p>
            If you opt in to text-message notifications, ZigZagX will use your mobile phone number solely to send you
            automated trade alerts related to trades and ideas you have chosen to follow inside the platform.
          </p>
          <p>
            <span className="text-zinc-300 font-medium">We do not share your mobile phone number with third parties</span>
            {" "}for their marketing purposes. We do not sell, rent, lease, or otherwise disclose mobile phone numbers or
            SMS consent information to any third party for promotional or advertising use. Numbers are transmitted only
            to our SMS delivery provider (Twilio) strictly for the purpose of delivering the alerts you requested, and
            no mobile opt-in data or consent records are shared with anyone else.
          </p>
          <p>
            <span className="text-zinc-300 font-medium">Message Frequency:</span> The number of text messages you
            receive is variable and depends entirely on how many trades you have chosen to follow inside the ZigZagX
            site. Active users who follow many trades may receive multiple messages per day; users who follow few or no
            trades may receive very few or no messages.
          </p>
          <p>
            <span className="text-zinc-300 font-medium">Message and Data Rates:</span> Message and data rates may apply
            depending on your mobile phone service plan. ZigZagX is not responsible for any carrier fees you may incur
            from receiving these messages.
          </p>
          <p>
            <span className="text-zinc-300 font-medium">Help &amp; Stop:</span> You may stop text notifications at any
            time from Settings → Trade Notifications inside the ZigZagX web platform. For help with text notifications,
            email us at{" "}
            <a href="mailto:admin@zigzagx.com" className="text-blue-400 hover:underline">admin@zigzagx.com</a>.
          </p>
        </Section>

        <Section title="Children's Privacy">
          <p>
            ZigZagX is not directed to children under 13, and we do not knowingly collect personal information from
            children. If we learn we have collected data from a child under 13, we will delete it promptly. Parents or
            guardians who believe a child has provided us information can contact us to have it removed.
          </p>
        </Section>

        <Section title="International Users">
          <p>
            ZigZagX is operated from the United States. If you access the Service from outside the U.S., you
            acknowledge that your information will be transferred to, stored, and processed in the United States,
            which may have different data protection laws than your country.
          </p>
        </Section>

        <Section title="Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time to reflect changes to our practices or for legal
            reasons. When we make material changes, we will update the "Updated" date at the top of this page and, if
            appropriate, notify you through the Service or by email. Continued use of the Service after changes take
            effect constitutes your acceptance of the updated policy.
          </p>
        </Section>

        <Section title="Contact Us">
          <p>Questions or requests about your data? We're happy to help.</p>
          <p>
            Via Email:{" "}
            <a href="mailto:admin@zigzagx.com" className="text-blue-400 hover:underline">admin@zigzagx.com</a>
          </p>
        </Section>

        <div className="mt-10 pt-6 border-t border-zinc-900 flex justify-between text-xs font-mono text-zinc-600 uppercase tracking-wider">
          <Link to="/login" className="hover:text-zinc-300" data-testid="privacy-footer-login">← Back to login</Link>
          <Link to="/terms" className="hover:text-zinc-300" data-testid="privacy-footer-terms">Terms of Service →</Link>
        </div>
      </main>
    </div>
  );
}
